import { Button } from '@gerdau/hefesto/Button';
import React, { useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../../../../assets/styles/theme/theme';
import { ApplicationState } from '../../../../store';
import {
  loadCustomerUsers,
  removeCustomerUser,
  updateCustomerUserPermission
} from '../../../../store/ducks/access-management/actions';
import { ICustomerUser } from '../../../../store/ducks/access-management/types';
import { ICustomer } from '../../../../store/ducks/customers/types';
import {
  ECustomerUserPermissionType,
  IUser
} from '../../../../store/ducks/user/types';
import AddUserModal from './AddUserModal';
import { Container, ErrorIcon, AddUserIcon, AddUserContainer } from './styles';
import CustomerUsersTable from './CustomerUsersTable';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { GetFlag } from '../../../../utils/featureFlag';

interface ICustomerUsersProps {
  customer: ICustomer;
}

const CustomerUsers: React.FC<ICustomerUsersProps> = ({ customer }) => {
  const dispatch = useDispatch();

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [canShowAddUserMessage, setCanShowAddUserMessage] = useState(false);

  const accessManagementState = useSelector(
    (applicationState: ApplicationState) => applicationState.acessManagement
  );

  const flagIgnoreRestrictions = GetFlag('ignore_user_management_restrictions');

  const restrictedToManageUsers =
    useSelector(
      (state: ApplicationState) => state.user.userRestrictions.usersManagement
    ) && !flagIgnoreRestrictions;
  const [customerUser, setCustomerUser] = useState<ICustomerUser>();
  const [alreadyInitialized, setAlreadyInitialized] = useState(false); // usado pra forçar o disparo da action loadCustomerUsers toda vez que expandir a linha

  const initCustomerUsers = () => {
    const foundCustomerUser = accessManagementState.customerUsers.find(
      (cu) => cu.customerId === customer.customerId
    );
    if (alreadyInitialized && foundCustomerUser) {
      setCustomerUser(foundCustomerUser);
    } else {
      dispatch(loadCustomerUsers(customer.customerId));
      setAlreadyInitialized(true);
    }
  };

  useEffect(() => {
    if (accessManagementState.customerUsers) {
      initCustomerUsers();
    }
  });

  const closeAddUserModalAfterCreateUser = () => {
    setShowAddUserModal(false);
    setCanShowAddUserMessage(false);
    setAlreadyInitialized(false);
  };

  useEffect(() => {
    if (
      canShowAddUserMessage &&
      !accessManagementState.errorCreateCustomerUser &&
      !accessManagementState.loadingCreateCustomerUser
    ) {
      closeAddUserModalAfterCreateUser();
    }
  });

  const updateUserPermission = (
    user: IUser,
    customerUserPermissionType: ECustomerUserPermissionType,
    updateType: 'add' | 'remove'
  ) => {
    dispatch(
      updateCustomerUserPermission(
        user,
        customer.customerId,
        updateType,
        customerUserPermissionType
      )
    );
  };
  const removeAllUserPermissions = (user: IUser) => {
    dispatch(removeCustomerUser(user, customer.customerId));
  };

  const openAddUserModal = () => {
    setShowAddUserModal(true);

    dispatch(
      sendTagging({
        screenName: 'LG:GestaoDeUsuarios',
        itemName: 'button:adicionar_usuario',
        itemDescription: 'gestaoDeUsuarios_adicionarUsuario',
        event: 'click'
      })
    );
  };

  return (
    <Container>
      {customerUser && !customerUser.errorUsers && (
        <>
          {!restrictedToManageUsers && (
            <AddUserContainer>
              <Button
                name=""
                onClick={() => openAddUserModal()}
                variant="secondary"
                disabled={customerUser?.loadingUsers}
                icon={<AddUserIcon />}
              >
                Adicionar usuário
              </Button>
            </AddUserContainer>
          )}
          {showAddUserModal && (
            <AddUserModal
              onClose={() => setShowAddUserModal(false)}
              setCanShowAddUserMessage={setCanShowAddUserMessage}
              customer={customer}
              accessManagementState={accessManagementState}
            />
          )}
        </>
      )}

      {customerUser && customerUser.loadingUsers && (
        <SkeletonTheme
          color={theme.colors.gray4}
          highlightColor={theme.colors.gray5}
        >
          <Skeleton count={5} width={'98.5%'} style={{ margin: '0.5rem 0' }} />
        </SkeletonTheme>
      )}

      {customerUser && customerUser.errorUsers && (
        <div className="error-content">
          <ErrorIcon />
          <span>Falha ao carregar os usuários</span>
        </div>
      )}

      {customerUser &&
        !customerUser.errorUsers &&
        !customerUser.loadingUsers &&
        customerUser.users && (
          <CustomerUsersTable
            users={customerUser.users}
            customerId={customer.customerId}
            updateUserPermission={updateUserPermission}
            removeAllUserPermissions={removeAllUserPermissions}
            isRemovingUser={accessManagementState.loadingRemoveUser}
          />
        )}
    </Container>
  );
};

export default CustomerUsers;
